import { useEffect, useState } from "react"
import { Outlet, useLocation, useNavigate } from "react-router"
import { client } from "@client/client"
import { DonorSidebar } from "./components/sidebar"
import { DonorMobileNav } from "./components/mobile-nav"
import { DonorWorkspaceSkeleton } from "./components/skeleton"
import {
  SponsorPortalContext,
  SponsorSessionContext,
  type SponsorPortal,
} from "./sponsor-session-context"

function SignOutIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className={className} aria-hidden>
      <path d="M15 17.625c-.074 1.852-1.617 3.424-3.684 3.374H6.423C4.256 20.999 2.5 19.395 2.5 17.392V6.608C2.5 4.605 4.256 3 6.423 3h4.893c2.043-.05 3.61 1.502 3.684 3.333M21.5 12H10.668M18.174 8.5 21.5 12l-3.326 3.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function DonorLayout() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [portal, setPortal] = useState<SponsorPortal | null>(null)
  const [checked, setChecked] = useState(false)

  const refresh = async () => {
    const response = await client.api.sponsor.portal.$get()
    if (response.status === 401) {
      navigate("/donor-signin", { replace: true })
      return
    }
    const body = await response.json()
    if (!response.ok || !("data" in body)) throw new Error("Could not load sponsor workspace")
    setPortal(body.data)
  }

  useEffect(() => {
    let cancelled = false
    const check = async () => {
      try {
        await refresh()
      } catch {
        if (!cancelled) navigate("/donor-signin", { replace: true })
      } finally {
        if (!cancelled) setChecked(true)
      }
    }
    void check()
    return () => {
      cancelled = true
    }
  }, [navigate])

  const logout = async () => {
    try {
      await client.api.sponsor.auth.logout.$post()
    } finally {
      navigate("/donor-signin", { replace: true })
    }
  }

  if (!checked || !portal) {
    return <DonorWorkspaceSkeleton />
  }

  return (
      <SponsorPortalContext.Provider value={{ data: portal, refresh }}>
      <SponsorSessionContext.Provider value={portal.sponsor}>
      <div className="flex h-screen overflow-hidden bg-cream-50">
        <DonorSidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-gold-500 bg-white px-4 py-3 sm:px-5 sm:py-4 lg:px-8 lg:py-5">
            <div className="min-w-0">
              <span className="text-[0.6875rem] font-semibold uppercase tracking-wider text-ink/60">SPONSOR AREA</span>
              <h1 className="truncate text-base font-semibold text-ink sm:text-lg">Welcome back, {portal.sponsor.name.split(" ")[0]}.</h1>
            </div>
            <button
              type="button"
              onClick={() => void logout()}
              className="inline-flex flex-none items-center gap-2 rounded-full border border-ink bg-transparent px-3 py-2 text-sm font-medium text-ink transition-colors hover:bg-cream-100 sm:px-4"
            >
              <SignOutIcon className="h-4 w-4" />
              Sign out
            </button>
          </header>
          <main className="flex-1 overflow-y-auto">
            <div key={pathname} className="w-full max-w-[960px] px-4 py-5 animate-fade-in-up sm:px-5 sm:py-6 lg:px-8 lg:py-8">
              <Outlet />
            </div>
          </main>
          <DonorMobileNav />
        </div>
      </div>
      </SponsorSessionContext.Provider>
    </SponsorPortalContext.Provider>
  )
}
